import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet, Tag, FileSpreadsheet, Printer } from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "../lib/fcfa";
import { exportFinanceExcel } from "../lib/excel";
import { printFinanceReport } from "../lib/documents";
import { useAuth } from "../contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function Finances() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [openTx, setOpenTx] = useState(false);
  const [openCat, setOpenCat] = useState(false);
  const [txForm, setTxForm] = useState({
    category_id: "", type: "recette", amount: "", description: "",
    transaction_date: new Date().toISOString().slice(0, 10), reference: "",
  });
  const [catForm, setCatForm] = useState({ name: "", type: "recette", color: "#C62828" });

  const load = async () => {
    const [c, t, s] = await Promise.all([
      api.get("/finance/categories"),
      api.get("/finance/transactions"),
      api.get("/finance/summary"),
    ]);
    setCategories(c.data);
    setTransactions(t.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const submitTx = async (e) => {
    e.preventDefault();
    const cat = categories.find((c) => c.id === txForm.category_id);
    try {
      await api.post("/finance/transactions", {
        ...txForm,
        category_id: txForm.category_id || null,
        category_name: cat?.name || null,
        amount: parseInt(txForm.amount, 10),
      });
      toast.success("Transaction enregistrée");
      setOpenTx(false);
      setTxForm({ ...txForm, amount: "", description: "", reference: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const submitCat = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/categories", catForm);
      toast.success("Catégorie créée");
      setOpenCat(false);
      setCatForm({ name: "", type: "recette", color: "#C62828" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDeleteTx = async (id) => {
    if (!confirm("Supprimer cette transaction ?")) return;
    await api.delete(`/finance/transactions/${id}`);
    load();
  };

  const onDeleteCat = async (id) => {
    if (!confirm("Supprimer cette catégorie ?")) return;
    await api.delete(`/finance/categories/${id}`);
    load();
  };

  const recettes = summary?.total_recette || 0;
  const depenses = summary?.total_depense || 0;
  const solde = summary?.solde || 0;

  return (
    <div>
      <PageHeader
        overline="Comptabilité FCFA"
        title="Finances"
        description="Suivez vos recettes, dépenses et bilan en franc CFA."
        actions={
          <>
            <Button variant="outline" onClick={() => exportFinanceExcel(transactions, summary || { total_recette: 0, total_depense: 0, solde: 0, by_category: [] })} data-testid="export-finance-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" onClick={() => printFinanceReport(user.club, summary || {}, transactions, "Toutes périodes")} data-testid="print-finance-report-btn">
              <Printer className="w-4 h-4 mr-2" /> Rapport PDF
            </Button>
            <Button variant="outline" onClick={() => setOpenCat(true)} data-testid="create-category-btn">
              <Tag className="w-4 h-4 mr-2" /> Catégorie
            </Button>
            <Button onClick={() => setOpenTx(true)} data-testid="create-transaction-btn" className="btn-club">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle transaction
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KPI icon={TrendingUp} label="Recettes" value={formatFCFA(recettes)} color="#2E7D32" testid="kpi-recettes" />
        <KPI icon={TrendingDown} label="Dépenses" value={formatFCFA(depenses)} color="#C62828" testid="kpi-depenses" />
        <KPI icon={Wallet} label="Solde" value={formatFCFA(solde)} color={solde >= 0 ? "#2E7D32" : "#C62828"} testid="kpi-solde" highlight />
      </div>

      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList className="bg-white border border-[#E0E0E0]">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Catégories</TabsTrigger>
          <TabsTrigger value="bilan" data-testid="tab-bilan">Bilan</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <div className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead className="bg-[#F4F5F7] text-[#616161] tracking-overline text-[10px]">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-left px-4 py-3">Catégorie</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-right px-4 py-3">Montant</th>
                    <th className="text-right px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E0E0E0]">
                  {transactions.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-[#616161]">Aucune transaction. Cliquez sur "Nouvelle transaction" pour commencer.</td></tr>
                  ) : transactions.map((t) => (
                    <tr key={t.id} className="hover:bg-[#F4F5F7]/50" data-testid={`transaction-row-${t.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{t.transaction_date}</td>
                      <td className="px-4 py-3">{t.description}</td>
                      <td className="px-4 py-3 text-[#616161]">{t.category_name || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={t.type === "recette" ? "bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30" : "bg-[#C62828]/10 text-[#C62828] border-[#C62828]/30"}>
                          {t.type === "recette" ? "Recette" : "Dépense"}
                        </Badge>
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold ${t.type === "recette" ? "text-[#2E7D32]" : "text-[#C62828]"}`}>
                        {t.type === "recette" ? "+" : "−"} {formatFCFA(t.amount)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => onDeleteTx(t.id)} data-testid={`delete-tx-${t.id}`}>
                          <Trash2 className="w-4 h-4 text-[#C62828]" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map((c) => (
              <div key={c.id} className="bg-white border border-[#E0E0E0] rounded-md p-4 flex items-center justify-between" data-testid={`category-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-10 rounded" style={{ backgroundColor: c.color }} />
                  <div>
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-[#616161] capitalize">{c.type}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => onDeleteCat(c.id)}>
                  <Trash2 className="w-4 h-4 text-[#C62828]" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bilan">
          <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
            <h3 className="font-display text-xl font-bold mb-4">Bilan par catégorie</h3>
            <div className="h-80" data-testid="bilan-chart">
              {summary?.by_category?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.by_category}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis dataKey="name" stroke="#616161" fontSize={12} interval={0} angle={-15} textAnchor="end" height={70} />
                    <YAxis stroke="#616161" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatFCFA(v)} />
                    <Legend />
                    <Bar dataKey="recette" stackId="a" fill="#2E7D32" name="Recettes" />
                    <Bar dataKey="depense" stackId="a" fill="#C62828" name="Dépenses" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-[#616161]">Pas encore de données.</div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Transaction dialog */}
      <Dialog open={openTx} onOpenChange={setOpenTx}>
        <DialogContent className="max-w-md" data-testid="tx-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouvelle transaction</DialogTitle></DialogHeader>
          <form onSubmit={submitTx} className="space-y-4">
            <div>
              <Label>Type *</Label>
              <Select value={txForm.type} onValueChange={(v) => setTxForm({ ...txForm, type: v, category_id: "" })}>
                <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recette">Recette</SelectItem>
                  <SelectItem value="depense">Dépense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={txForm.category_id || "none"} onValueChange={(v) => setTxForm({ ...txForm, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="tx-category"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sans catégorie</SelectItem>
                  {categories.filter((c) => c.type === txForm.type).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (FCFA) *</Label>
              <Input type="number" required min={0} value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} data-testid="tx-amount" />
            </div>
            <div>
              <Label>Description *</Label>
              <Input required value={txForm.description} onChange={(e) => setTxForm({ ...txForm, description: e.target.value })} data-testid="tx-description" />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" required value={txForm.transaction_date} onChange={(e) => setTxForm({ ...txForm, transaction_date: e.target.value })} data-testid="tx-date" />
            </div>
            <div>
              <Label>Référence</Label>
              <Input value={txForm.reference} onChange={(e) => setTxForm({ ...txForm, reference: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenTx(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="tx-submit-btn">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={openCat} onOpenChange={setOpenCat}>
        <DialogContent className="max-w-md" data-testid="cat-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouvelle catégorie</DialogTitle></DialogHeader>
          <form onSubmit={submitCat} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input required value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-name" />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={catForm.type} onValueChange={(v) => setCatForm({ ...catForm, type: v })}>
                <SelectTrigger data-testid="cat-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recette">Recette</SelectItem>
                  <SelectItem value="depense">Dépense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Couleur</Label>
              <Input type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCat(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="cat-submit-btn">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testid, highlight }) {
  return (
    <div className={`bg-white border rounded-md p-5 ${highlight ? "border-[#C62828]/40" : "border-[#E0E0E0]"}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-overline text-[#616161]">{label}</p>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display text-2xl font-extrabold tracking-tight" style={{ color }}>{value}</p>
    </div>
  );
}
